import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"
import type { FinancialChartDatum } from "../../types/financialUi"
import {
  FINANCIAL_CHART_RESIZE_DEBOUNCE_MS,
  hasFinancialChartData,
  sanitizeFinancialSeries,
} from "../../utils/financialChartUtils"
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion"
import { FinancialEmptyState } from "../FinancialEmptyState"

interface FinancialDonutChartProps {
  data: readonly FinancialChartDatum[]
  ariaLabel: string
  emptyTitle: string
  emptyDescription?: string
  formatValue: (value: number) => string
}

export const FinancialDonutChart: React.FC<FinancialDonutChartProps> = ({
  data,
  ariaLabel,
  emptyTitle,
  emptyDescription,
  formatValue,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const chartData = sanitizeFinancialSeries(data).filter((item) => item.value > 0)
  const total = chartData.reduce((sum, item) => sum + item.value, 0)

  if (!hasFinancialChartData(chartData)) {
    return <FinancialEmptyState title={emptyTitle} description={emptyDescription} />
  }

  return (
    <div className="grid min-h-72 grid-cols-1 items-center gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(210px,0.8fr)]" role="img" aria-label={ariaLabel}>
      <div className="h-64 min-w-0 xl:h-72">
        <ResponsiveContainer width="100%" height="100%" debounce={FINANCIAL_CHART_RESIZE_DEBOUNCE_MS}>
          <PieChart accessibilityLayer>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="label"
              innerRadius="58%"
              outerRadius="88%"
              paddingAngle={3}
              cornerRadius={7}
              stroke="#fff"
              strokeWidth={3}
              style={{ filter: "drop-shadow(0 10px 16px rgba(15, 23, 42, 0.10))" }}
              isAnimationActive={!prefersReducedMotion}
              animationDuration={950}
              animationEasing="ease-out"
            >
              {chartData.map((item) => (
                <Cell key={item.label} fill={item.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => formatValue(Number(value ?? 0))}
              contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0", boxShadow: "0 10px 30px rgba(15,23,42,.10)" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className="space-y-2.5" aria-label={ariaLabel}>
        {chartData.map((item) => (
          <li key={item.label} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 transition duration-200 hover:-translate-y-0.5 hover:bg-orange-50 hover:shadow-sm motion-reduce:transform-none motion-reduce:transition-none">
            <span className="flex min-w-0 items-center gap-2 text-sm font-bold text-slate-700">
              <span className="h-3 w-3 shrink-0 rounded-full shadow-sm" style={{ backgroundColor: item.color }} />
              <span className="truncate" title={item.label}>{item.label}</span>
            </span>
            <span className="shrink-0 text-sm font-black text-slate-900">
              {total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : "0%"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
